import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    Box,
    Grid,
    IconButton,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import Swal from 'sweetalert2';
import {
    EmailTemplateBuilderServiceQuery,
    LocalizationManagementQuery,
    MasterDataQuery,
} from '../../../services/query';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUITextFieldWithButton } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailTemplateBuilderCommand from '../../../services/command/emailTemplateBuilder/EmailTemplateBuilderCommand.services';
import { useHistory, useLocation } from 'react-router-dom';
import { ETBTG, etbCls } from '../emailTemplateBuilder.classes';

export const TemplateGallery: FC<any> = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const [filteredDatas, setFilteredDatas] = React.useState<any>();
    const [allRowCount, setAllRowCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [countryList, setCountryList] = useState<any>();
    const [selectedCountry, setSelectedCountry] = useState<any>(searchParams.get('countryCode') || 'TR');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [languageList, setLanguageList] = useState<any>();
    const [selectedLanguage, setSelectedLanguage] = useState(searchParams.get('languageCode') || 'tr-TR');
    const handleDeleteClick = async (templateId: any) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: t('emailTemplateBuilder.deleteTemplateTitle'),
            text: t('emailTemplateBuilder.deleteTemplateConfirmation'),
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.no'),
        });

        if (!result.isConfirmed) {
            return;
        }

        const req = { templateId: templateId };
        await EmailTemplateBuilderCommand.DeleteTemplate(req);
        await Swal.fire({
            icon: 'success',
            title: t('general.deleted'),
            text: t('emailTemplateBuilder.templateDeleted'),
        });
        getTemplates();
    };

    const handlePageChange = (_: any, newPage: number) => {
        setCurrentPage(newPage);
    };

    const getTemplates = async (req?: any) => {
        if (!req) {
            const take = 10;
            let skip = 1;
            if (currentPage > 1) {
                skip = currentPage;
            }

            req = {
                key: searchKeyword,
                take: take,
                skip: skip,
                languageCode: selectedLanguage,
                countryCode: selectedCountry,
            };
        }
        try {
            const response = await EmailTemplateBuilderServiceQuery.GetTemplatesData(req);
            if (response?.data?.length > 0) {
                let parseData = response.data
                    .map((item: any) => {
                        return {
                            id: item.id,
                            createOn: item.createOn,
                            name: item.name || '-',
                            key: item.key || '-',
                            createdBy: item.createdBy || '-',
                            subject: item.subject || '-',
                            sections: item.sections,
                            countryCode: item.countryCode,
                            languageCode: item.languageCode,
                        };
                    })
                    .flat();
                setAllRowCount(response.totalPages);
                setFilteredDatas(parseData);
            } else {
                setAllRowCount(0);
                setFilteredDatas([]);
                Swal.fire({
                    icon: 'info',
                    title: t('general.noDataFound'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.errorOccurred'),
            });
        }
    };

    useEffect(() => {
        GetCountryList();
        GetLanguageList();
    }, []);

    useEffect(() => {
        if (countryList?.length > 0 && languageList?.length > 0) {
            const urlCountry = searchParams.get('countryCode');
            const urlLanguage = searchParams.get('languageCode');

            if (urlCountry && countryList.some((c: any) => c.name === urlCountry)) {
                setSelectedCountry(urlCountry);
            }

            if (urlLanguage && languageList.some((l: any) => l.code === urlLanguage)) {
                setSelectedLanguage(urlLanguage);
            }
        }
    }, [countryList, languageList]);

    // Dil ve ülke değişiminde otomatik istek atılmasını kaldır
    useEffect(() => {
        updateURL();
    }, [selectedCountry, selectedLanguage]);

    // İlk yükleme ve sayfa değişiminde veri getir
    useEffect(() => {
        if (selectedCountry && selectedLanguage) {
            getTemplates();
        }
    }, [currentPage, selectedCountry, selectedLanguage]);

    const updateURL = () => {
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('languageCode', selectedLanguage);
        newSearchParams.set('countryCode', selectedCountry);
        history.replace({
            pathname: location.pathname,
            search: newSearchParams.toString(),
        });
    };

    const GetCountryList = async () => {
        MasterDataQuery.GetCountryList().then((res: any) => {
            let parseData = res.data.map((item: any) => {
                return {
                    id: item.countryId,
                    key: item.countryKey,
                    name: item.countryCode,
                    dropdownValueText: item.translatedCountryName,
                };
            });
            setCountryList(parseData);
        });
    };

    const GetLanguageList = async () => {
        LocalizationManagementQuery.CultureList().then((res: any) => {
            setLanguageList(res.data);
        });
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getTemplates({
                key: searchKeyword,
                take: 10,
                skip: 1,
                languageCode: selectedLanguage,
                countryCode: selectedCountry,
            });
            setCurrentPage(1);
        }
    };

    const handleFilter = () => {
        getTemplates({
            key: searchKeyword,
            take: 10,
            skip: 1,
            languageCode: selectedLanguage,
            countryCode: selectedCountry,
        });
        setCurrentPage(1);
    };

    return (
        <SCUIContent>
            <Box className={ETBTG.page}>
            <SCUIBreadCrumb
                dontShowHome={true}
                homeIconLink={'/home'}
                onlyTitle={t('emailTemplateBuilder.templateGallery')}
                components={[
                    {
                        title: t('emailTemplateBuilder.templateBuilder'),
                        link: '/emailTemplateBuilder',
                    },
                    {
                        title: t('emailTemplateBuilder.templateGallery'),
                        link: `/emailTemplateBuilder/templateGallery`,
                    },
                ]}
            />
            <div className={etbCls('lineWrapper', ETBTG.divider)} />
            <Grid container spacing={2} className={ETBTG.toolbarGrid} sx={{ mb: 2 }}>
                <Grid
                    container
                    spacing={2}
                    className={ETBTG.primaryActionsGrid}
                    size={{ xs: 12 }}
                    style={{ marginTop: '1.2rem' }}
                >
                    <Grid size={{ xs: 12 }}>
                        <SCUIButton
                            className={ETBTG.inputButtonCreateTemplate}
                            text={t('emailTemplateBuilder.createNewTemplate')}
                            type="primary"
                            sizeType="default"
                            size={'s'}
                            href={'/emailTemplateBuilder/createTemplate'}
                            showStartIcon
                            startIcon={<AddCircleOutlineIcon style={{ color: 'white', marginRight: '8px' }} />}
                            style={{ whiteSpace: 'nowrap', width: '20%' }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SCUIButton
                            className={ETBTG.inputButtonCreateSection}
                            text={t('emailTemplateBuilder.createNewSection')}
                            type="primary"
                            sizeType="default"
                            size={'s'}
                            href={'/emailTemplateBuilder/addSection'}
                            showStartIcon
                            startIcon={<AddCircleOutlineIcon style={{ color: 'white', marginRight: '8px' }} />}
                            style={{ whiteSpace: 'nowrap', width: '20%' }}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2} className={ETBTG.filtersRow} size={{ xs: 12 }} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 3 }}>
                        {countryList && countryList.length > 0 && (
                            <Box className={ETBTG.fieldCountry}>
                            <Autocomplete
                                className={ETBTG.autocompleteCountryRoot}
                                options={countryList || []}
                                getOptionLabel={(option: any) => option?.dropdownValueText || ''}
                                isOptionEqualToValue={(option: any, value: any) => option?.name === value?.name}
                                value={countryList.find((item: any) => item.name === selectedCountry) || null}
                                onChange={(_, newValue: any) => {
                                    setSelectedCountry(newValue?.name || '');
                                }}
                                slotProps={{
                                    popper: {
                                        sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className={ETBTG.inputAutocompleteCountry}
                                        label={t('emailTemplateBuilder.selectCountryName')}
                                        size="small"
                                        fullWidth
                                    />
                                )}
                                disablePortal
                                fullWidth
                            />
                            </Box>
                        )}
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        {languageList && languageList.length > 0 && (
                            <Box className={ETBTG.fieldLanguage}>
                            <Autocomplete
                                className={ETBTG.autocompleteLanguageRoot}
                                options={languageList}
                                getOptionLabel={(option: any) => option.code}
                                value={languageList.find((item: any) => item.code === selectedLanguage) || null}
                                onChange={(_, newValue) => {
                                    setSelectedLanguage(newValue?.code || 'tr-TR');
                                }}
                                slotProps={{
                                    popper: {
                                        sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className={ETBTG.inputAutocompleteLanguage}
                                        label={t('emailTemplateBuilder.selectLanguage')}
                                        size="small"
                                        fullWidth
                                    />
                                )}
                                disablePortal
                            />
                            </Box>
                        )}
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Box className={ETBTG.searchCompositeWrap}>
                        <SCUITextFieldWithButton
                            buttonText={t('general.filter')}
                            placeholder={t('general.search')}
                            value={searchKeyword}
                            onChange={handleSearch}
                            onKeyPress={handleKeyPress}
                            onClick={handleFilter}
                        />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <TableContainer className={ETBTG.tableContainer}>
                <Table component={Paper} className={ETBTG.table} style={{ marginBottom: '2rem' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ textAlign: 'center', minWidth: 250 }}>
                                {t('emailTemplateBuilder.templateName')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 200 }}>
                                {t('emailTemplateBuilder.description')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 200 }}>
                                {t('emailTemplateBuilder.languageCode')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 200 }}>
                                {t('emailTemplateBuilder.countryCode')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 80 }}>
                                {t('emailTemplateBuilder.sendTestMail')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 20 }}>{''}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDatas &&
                            filteredDatas.map((emailTemplate: any) => (
                                <TableRow key={emailTemplate.id} className={ETBTG.tableRow}>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.name}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.key}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.countryCode}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.languageCode}</TableCell>

                                    <TableCell>
                                        <div className={ETBTG.sendTestMailCellWrap} style={{ display: 'flex', justifyContent: 'center' }}>
                                            <SCUIButton
                                                className={ETBTG.inputButtonSendTestMail}
                                                text={t('emailTemplateBuilder.sendTestMail')}
                                                type="primary"
                                                sizeType="fixed"
                                                size={'s'}
                                                href={`/emailTemplateBuilder/send-test-mail/${emailTemplate.key}?languageCode=${selectedLanguage}&countryCode=${selectedCountry}`}
                                                showStartIcon
                                                startIcon={<SendIcon style={{ color: 'white' }}></SendIcon>}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            className={ETBTG.inputIconDeleteTemplate}
                                            style={{ color: '#03a9f4' }}
                                            onClick={() => handleDeleteClick(emailTemplate.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <Grid className={ETBTG.paginationWrap}>
                    <Stack spacing={2} style={{ alignItems: 'end' }}>
                        <Pagination
                            count={allRowCount}
                            color="primary"
                            page={currentPage}
                            onChange={handlePageChange}
                        />
                    </Stack>
                </Grid>
            </TableContainer>
            </Box>
        </SCUIContent>
    );
};
